import { apiClient, tokenStorage, unauthenticatedClient } from './baseApi';

export type AuthUser = {
  id: number;
  full_name: string;
  id_number: string;
  phone: string;
  email: string | null;
  status: string;
  is_admin: boolean;
  date_applied: string | null;
  date_joined: string | null;
  created_at: string | null;
  reason_for_joining?: string | null;
};

type LoginPayload = {
  phone: string;
  password: string;
  remember?: boolean;
};

type LoginResponse = {
  message: string;
  access_token: string;
  refresh_token: string;
  user: AuthUser;
};

type ResetPasswordRequestPayload = {
  phone: string;
};

type ResetPasswordPayload = {
  phone: string;
  otp: string;
  new_password: string;
};

type ChangePasswordPayload = {
  current_password: string;
  new_password: string;
};

type TokenResponse = {
  access_token: string;
};

export const AuthApi = {
  login: async ({ phone, password, remember = true }: LoginPayload): Promise<LoginResponse> => {
    const response = await unauthenticatedClient.post<LoginResponse>('/auth/login', {
      phone,
      password,
    });

    tokenStorage.setTokens(response.data.access_token, response.data.refresh_token, remember);

    return response.data;
  },

  logout: () => {
    tokenStorage.clear();
  },

  refreshAccessToken: async (): Promise<TokenResponse> => {
    const refreshToken = tokenStorage.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token found');
    }

    const response = await unauthenticatedClient.post<TokenResponse>(
      '/auth/refresh',
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      },
    );

    tokenStorage.setAccessToken(response.data.access_token);

    return response.data;
  },

  me: async (): Promise<AuthUser> => {
    const response = await apiClient.get<{ user: AuthUser }>('/auth/me');
    return response.data.user;
  },

  requestPasswordReset: async ({ phone }: ResetPasswordRequestPayload) => {
    const response = await unauthenticatedClient.post('/auth/reset-password-request', { phone });
    return response.data;
  },

  resetPassword: async ({ phone, otp, new_password }: ResetPasswordPayload) => {
    const response = await unauthenticatedClient.post('/auth/reset-password', {
      phone,
      otp,
      new_password,
    });
    return response.data;
  },

  changePassword: async (payload: ChangePasswordPayload) => {
    const response = await apiClient.post('/auth/change-password', payload);
    return response.data;
  },

  getProfile: async (): Promise<{ profile: AuthUser; message: string }> => {
    const response = await apiClient.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (payload: { email?: string }): Promise<{ profile: AuthUser; message: string }> => {
    const response = await apiClient.put('/auth/profile', payload);
    return response.data;
  },
};

export default AuthApi;

